import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IOrdersPagination } from '../../../graphQL/queries/getOrders/types'
import { IOrderDetail } from '../../../graphQL/queries/order/types'
import {
  DEFAULT_ORDERS_PAGE_SIZE,
  IOrderCreationFailure,
  IOrdersState,
  OrdersPagePayload,
} from './types'

export const initialPagination: IOrdersPagination = {
  total: 0,
  totalPages: 0,
  pageNumber: 1,
  pageSize: DEFAULT_ORDERS_PAGE_SIZE,
  hasNextPage: false,
  nextPage: null,
}

export const initialState: IOrdersState = {
  list: [],
  pagination: { ...initialPagination },
  activeFilter: '',
  status: 'idle',
  isLoadingMore: false,
  loadMoreError: null,
  selected: null,
  selectedStatus: 'idle',
  isPollingOrder: false,
  pollError: null,
  pollTimedOut: false,
  creationStatus: 'idle',
  created: null,
  creationError: null,
  failedCheckoutRules: [],
}

/**
 * Defence in depth: the GraphQL contract declares `orders` as a nullable list,
 * so a null payload is reachable. Coerce to `[]` rather than letting it reach
 * `.concat` or `.length`.
 */
const safeOrders = (payload?: OrdersPagePayload) =>
  Array.isArray(payload?.orders) ? payload.orders : []

export const ordersSlice = createSlice({
  name: 'ordersSlice',
  initialState,
  reducers: {
    /**
     * First page, or a retry after an error. Skeletons replace the list.
     */
    setOrdersLoading: (state) => {
      state.status = 'loading'
      state.isLoadingMore = false
      state.loadMoreError = null
    },
    /**
     * A page-1 response. Replaces the list wholesale.
     *
     * An empty page is a success: `list: []` with `status: 'ready'` is the
     * empty state the UI renders, never an error.
     */
    setOrders: (state, { payload }: PayloadAction<OrdersPagePayload>) => {
      state.list = safeOrders(payload)
      state.pagination = payload?.pagination
        ? { ...initialPagination, ...payload.pagination }
        : { ...initialPagination }
      state.status = 'ready'
      state.isLoadingMore = false
      state.loadMoreError = null
    },
    /**
     * A load-more page is in flight. `status` is left alone on purpose so the
     * rows and the sentinel stay mounted.
     */
    setOrdersLoadingMore: (state) => {
      state.isLoadingMore = true
      state.loadMoreError = null
    },
    /**
     * A load-more response. Concatenates onto the rows already on screen and
     * holds `status: 'ready'` throughout.
     */
    appendOrders: (state, { payload }: PayloadAction<OrdersPagePayload>) => {
      state.list = state.list.concat(safeOrders(payload))
      if (payload?.pagination) {
        state.pagination = { ...state.pagination, ...payload.pagination }
      }
      state.status = 'ready'
      state.isLoadingMore = false
      state.loadMoreError = null
    },
    /**
     * A status tab was selected. Resetting the list and the pagination in the
     * same transition as the flip to `'loading'` means no frame ever renders
     * the previous filter's rows underneath the newly selected tab.
     */
    setOrdersFilter: (state, { payload }: PayloadAction<string>) => {
      state.activeFilter = payload
      state.list = []
      state.pagination = { ...initialPagination }
      state.status = 'loading'
      state.isLoadingMore = false
      state.loadMoreError = null
    },
    /**
     * A first-page failure. `OrderList` renders its error state with a retry
     * action in place of the list.
     */
    setOrdersError: (state) => {
      state.status = 'error'
      state.isLoadingMore = false
    },
    /**
     * A load-more failure. The pages already fetched stay on screen and the
     * sentinel stays mounted, so scrolling again retries.
     */
    setOrdersLoadMoreError: (
      state,
      { payload }: PayloadAction<string | undefined>,
    ) => {
      state.isLoadingMore = false
      state.loadMoreError = payload ?? 'LOAD_MORE_FAILED'
    },
    /**
     * Opening the Detail screen, or retrying after a failure.
     *
     * Clears `selected` in the same transition as the flip to `'loading'`, for
     * the reason `setOrdersFilter` clears the list: the entity on screen is
     * about to change, and no frame should render the previous order's totals
     * underneath the newly opened one.
     */
    setOrderDetailLoading: (state) => {
      state.selected = null
      state.selectedStatus = 'loading'
      state.isPollingOrder = false
      state.pollError = null
      state.pollTimedOut = false
    },
    /**
     * A detail response. Unlike a page of history, an absent order is not a
     * valid empty state — see `setOrderDetailNotFound`.
     */
    setOrderDetail: (state, { payload }: PayloadAction<IOrderDetail>) => {
      state.selected = payload
      state.selectedStatus = 'ready'
      state.isPollingOrder = false
      state.pollError = null
    },
    /**
     * A poll tick is in flight. `selectedStatus` is left alone on purpose so
     * the rendered order survives the tick.
     */
    setOrderDetailPolling: (state) => {
      state.isPollingOrder = true
      state.pollError = null
    },
    /**
     * A poll tick came back. Replaces the order in place — a tick re-reads the
     * same order rather than adding to it, which is where this parts company
     * with `appendOrders`.
     */
    setOrderDetailPollResult: (
      state,
      { payload }: PayloadAction<IOrderDetail>,
    ) => {
      state.selected = payload
      state.selectedStatus = 'ready'
      state.isPollingOrder = false
      state.pollError = null
    },
    /**
     * The initial fetch failed. The panel renders its error state with a retry.
     */
    setOrderDetailError: (state) => {
      state.selectedStatus = 'error'
      state.isPollingOrder = false
    },
    /**
     * The order does not exist, or does not belong to this shopper. The server
     * answers both with the same 404, so this status claims no more than that.
     *
     * Distinct from `setOrderDetailError`, which is a request that failed: both
     * leave `selected` null, and `OrderDetailPanel` renders the two states with
     * different messages, so the status has to carry which one happened.
     */
    setOrderDetailNotFound: (state) => {
      state.selected = null
      state.selectedStatus = 'notFound'
      state.isPollingOrder = false
    },
    /**
     * There is no session to scope the read to, so no request was made.
     *
     * Not an error: nothing failed, and a retry would fail the same way. The
     * panel's `sessionEnded` state says so and offers no retry, which is why
     * this cannot be folded into `setOrderDetailError`.
     */
    setOrderDetailSessionEnded: (state) => {
      state.selected = null
      state.selectedStatus = 'sessionEnded'
      state.isPollingOrder = false
    },
    /**
     * A poll tick failed. The order already on screen stays there and polling
     * can tick again, so a single dropped request is not fatal to the screen.
     */
    setOrderDetailPollError: (
      state,
      { payload }: PayloadAction<string | undefined>,
    ) => {
      state.isPollingOrder = false
      state.pollError = payload ?? 'ORDER_POLL_FAILED'
    },
    /**
     * Polling hit its deadline with the order still `PROCESSING`. The order
     * stays on screen; only the pending affordance changes.
     */
    setOrderDetailTimedOut: (state) => {
      state.isPollingOrder = false
      state.pollTimedOut = true
    },
    /**
     * `createOrder` is in flight.
     *
     * Clears every field of the write half in the same transition, for the
     * reason `setOrderDetailLoading` clears `selected`: a retry after a
     * failure must not render the previous attempt's decline message
     * underneath a spinner for the new one.
     */
    setOrderCreating: (state) => {
      state.creationStatus = 'creating'
      state.created = null
      state.creationError = null
      state.failedCheckoutRules = []
    },
    /**
     * `createOrder` returned, and the flow is going on to confirm or to poll.
     *
     * The order is stored without a terminal verdict: the status it came back
     * with is what selects the mechanism, and that decision belongs to the
     * flow rather than to a reducer.
     */
    setOrderConfirming: (state, { payload }: PayloadAction<IOrderDetail>) => {
      state.created = payload
      state.creationStatus = 'confirming'
      state.creationError = null
    },
    /**
     * A poll tick came back with the order still unsettled.
     *
     * Replaces the order in place and holds `'confirming'`, the way
     * `setOrderDetailPollResult` holds `'ready'`: the screen is already
     * rendering this order and a tick must not blank it.
     *
     * A failed tick has no action of its own. The read half needs one because
     * a dropped tick there leaves an order on screen whose staleness the
     * shopper cannot see; here the screen is already saying "confirming", the
     * poll ticks again, and the budget is what ends it. Recording each failure
     * would add a field nothing renders.
     */
    setOrderCreationPollResult: (
      state,
      { payload }: PayloadAction<IOrderDetail>,
    ) => {
      state.created = payload
      state.creationStatus = 'confirming'
    },
    /**
     * Terminal: the order was placed and this storefront does not confirm
     * asynchronously. Not a lesser success than `confirmed` — it is the whole
     * outcome for a storefront that declared no confirmation.
     */
    setOrderCreated: (state, { payload }: PayloadAction<IOrderDetail>) => {
      state.created = payload
      state.creationStatus = 'created'
      state.creationError = null
      state.failedCheckoutRules = []
    },
    /**
     * Terminal: the order reached a configured success status.
     */
    setOrderConfirmed: (state, { payload }: PayloadAction<IOrderDetail>) => {
      state.created = payload
      state.creationStatus = 'confirmed'
      state.creationError = null
      state.failedCheckoutRules = []
    },
    /**
     * Terminal: creation or confirmation failed, definitely.
     *
     * `created` is replaced when the payload carries an order and left as it
     * stands otherwise. Both cases are real: a poll tick that read a failure
     * status holds the order the verdict is about, and keeping the previous
     * tick's copy would render `failed` beside a status that says otherwise;
     * a confirmation decline leaves the created order untouched and correct;
     * and a creation failure never set it at all.
     */
    setOrderCreationFailed: (
      state,
      { payload }: PayloadAction<IOrderCreationFailure | undefined>,
    ) => {
      state.creationStatus = 'failed'
      state.creationError = payload?.message ?? null
      state.failedCheckoutRules = payload?.failedCheckoutRules ?? []
      if (payload?.order) state.created = payload.order
    },
    /**
     * Terminal: the poll spent its budget with the order still unsettled.
     *
     * Separate from `setOrderCreationFailed` because the facts differ — the
     * order exists and only its confirmation is unknown — and the screen owes
     * the shopper a refresh rather than an apology.
     */
    setOrderOutcomeUnknown: (state) => {
      state.creationStatus = 'unknown'
    },
    /**
     * Leaving the confirmation screen, or arming a fresh attempt.
     *
     * Resets the write half only; the history list and the detail half are
     * untouched, mirroring `clearOrderDetail`.
     */
    clearOrderCreation: (state) => {
      state.creationStatus = 'idle'
      state.created = null
      state.creationError = null
      state.failedCheckoutRules = []
    },
    /**
     * Leaving the Detail screen. Resets the detail half only — the history
     * list, its pagination and its filter are untouched.
     *
     * That the list survives is not why History is cheap to return to: the
     * template fetches unconditionally on mount either way
     * (`templates/OrderHistory.tsx`). What it buys is that no frame renders
     * History with the detail half's leftovers still in the store.
     */
    clearOrderDetail: (state) => {
      state.selected = null
      state.selectedStatus = 'idle'
      state.isPollingOrder = false
      state.pollError = null
      state.pollTimedOut = false
    },
  },
})

export const {
  setOrdersLoading,
  setOrders,
  setOrdersLoadingMore,
  appendOrders,
  setOrdersFilter,
  setOrdersError,
  setOrdersLoadMoreError,
  setOrderDetailLoading,
  setOrderDetail,
  setOrderDetailPolling,
  setOrderDetailPollResult,
  setOrderDetailError,
  setOrderDetailNotFound,
  setOrderDetailSessionEnded,
  setOrderDetailPollError,
  setOrderDetailTimedOut,
  clearOrderDetail,
  setOrderCreating,
  setOrderConfirming,
  setOrderCreationPollResult,
  setOrderCreated,
  setOrderConfirmed,
  setOrderCreationFailed,
  setOrderOutcomeUnknown,
  clearOrderCreation,
} = ordersSlice.actions

export default ordersSlice.reducer
